import { useState, type FormEvent } from 'react';
import { LIMITS, displayTag, type RoomId } from '@engineer-os/chat';
import { useStrings } from '../../state/useLocale';
import { Button, Machine } from '../../ui';
import type { TeamsApi } from '../../state/useTeams';
import { Avatar } from './Identity';

/**
 * The teams this learner is in, the invitations waiting for them, and the one
 * form that makes a new one.
 *
 * ## Why this is one popover and not three places
 *
 * A team is a room, so the room picker is where the list obviously belongs; an
 * invitation is somebody wanting something from you, so the inbox is where
 * that obviously belongs. Both are true and following them would have put a
 * brand-new idea in two halves across two surfaces a learner already knows for
 * other reasons — and the half in the picker would sit under a heading about
 * occupancy, next to a number field, explaining nothing about what a team is.
 * One concept, one place to learn it. The picker and the inbox keep doing
 * exactly what they did.
 *
 * ## What it says out loud, and why
 *
 * **A team room is private from other learners and not from whoever runs the
 * deployment.** The note under the roster says so, in the panel, rather than
 * only in `docs/teams.md`. This is the first room in the product that looks
 * private, and a room that looks private while being readable by an operator
 * with a token is the exact situation where somebody says something they would
 * not have said. The moderation path is the *reason* the private half of this
 * feature is a room at all — report, hide and ban work in here unchanged,
 * because a report carries a room and a message id — so the sentence is not an
 * apology for a limitation. It is a description of the trade that makes this
 * shippable.
 *
 * ## Invitations, and the shape of "no"
 *
 * Nobody is ever put in a team. A lead sends an invitation, it waits here, and
 * the two answers are one press each and equally weighted: Join and Decline
 * are the same size, and declining says nothing back to whoever asked. Being
 * added to a group chat without being asked is the single most common way a
 * group feature becomes a nuisance, and a decline that pages the person who
 * was refused turns "no" into a conversation.
 */
export interface TeamsProps {
  teams: TeamsApi;
  /** False while signed out: a team is an account's, so there is nothing to show. */
  signedIn: boolean;
  /** Open a team's room. The id comes off the wire; the panel never builds one. */
  onOpen(room: RoomId): void;
  /** Which room is on screen, so the team whose room it is reads as current. */
  active: RoomId;
}

export function Teams({ teams, signedIn, onOpen, active }: TeamsProps) {
  const s = useStrings().chat;
  const [making, setMaking] = useState(false);
  const [name, setName] = useState('');
  // The last refusal, as the Worker worded it. One at a time: there is one
  // form and one list, and a stack of errors in a 280px popover is a wall.
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!signedIn) {
    return (
      <div className="chat-teams">
        <p className="note">{s.teams.signedOut}</p>
      </div>
    );
  }

  const full = teams.teams.length >= LIMITS.team.perLearner;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    const done = await teams.create(name.trim());
    setBusy(false);
    if (!done.ok) {
      setFailed(reason(done.reason, s));
      return;
    }
    setName('');
    setMaking(false);
    setFailed(null);
    if (done.room) onOpen(done.room);
  };

  const answer = async (id: number, accept: boolean) => {
    if (busy) return;
    setBusy(true);
    const done = await teams.answer(id, accept);
    setBusy(false);
    if (!done.ok) setFailed(reason(done.reason, s));
    else if (accept && done.room) onOpen(done.room);
  };

  return (
    <div className="chat-teams">
      {/* --- Asked to join ------------------------------------------------
          First, because it is the only thing here that somebody else started
          and the only thing waiting on an answer. */}
      {teams.invites.length > 0 && (
        <section className="chat-teams-block">
          <p className="label">{s.teams.asked}</p>
          {teams.invites.map((i) => (
            <div key={i.id} className="chat-invite">
              <p className="chat-invite-line">
                {/* Both names are people's or their own, and either may be in
                    either script, so each carries its own direction and is
                    isolated from the words around it (docs/i18n.md §7). */}
                <span dir="auto">{i.fromName}</span>
                {i.fromTag && <Machine className="msg-tag">#{displayTag(i.fromTag)}</Machine>}
              </p>
              <p className="chat-invite-team">
                <span dir="auto">{i.teamName}</span>
                <span className="msg-meta">{s.teams.size(i.size)}</span>
              </p>
              <div className="chat-invite-actions">
                {/* The same size, deliberately. "No" is not a lesser answer. */}
                <Button type="button" variant="solid" disabled={busy} onClick={() => void answer(i.id, true)}>
                  {s.teams.join}
                </Button>
                <Button type="button" variant="quiet" disabled={busy} onClick={() => void answer(i.id, false)}>
                  {s.teams.decline}
                </Button>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* --- The teams themselves ----------------------------------------- */}
      <section className="chat-teams-block">
        <p className="label">{s.teams.mine}</p>
        {teams.teams.length === 0 ? (
          <p className="note">{teams.loading ? s.teams.loading : s.teams.none}</p>
        ) : (
          teams.teams.map((t) => (
            <div key={t.id} className={t.room === active ? 'chat-team is-here' : 'chat-team'}>
              <button type="button" className="chat-team-open" onClick={() => onOpen(t.room)}>
                <span className="chat-team-name" dir="auto">
                  {t.name}
                </span>
                <span className="msg-meta">{s.teams.size(t.members.length)}</span>
              </button>
              {/* The roster as faces and names, which is what a team is. No
                  levels and no ranks: this is who is in the room, not a
                  scoreboard, and the card behind any of these names has the
                  record on it for anybody who wants it. */}
              <ul className="chat-team-roster">
                {t.members.map((m) => (
                  <li key={m.tag ?? m.name} className="chat-team-member">
                    <Avatar src={m.avatarSeed} tag={m.tag ?? undefined} />
                    <span className="msg-name" dir="auto">
                      {m.name}
                    </span>
                    {m.role === 'lead' && <span className="msg-meta">{s.teams.lead}</span>}
                  </li>
                ))}
              </ul>
              <div className="chat-team-actions">
                {t.role === 'lead' && <span className="note">{s.teams.youLead}</span>}
                <button
                  type="button"
                  className="chat-link"
                  disabled={busy}
                  onClick={() => {
                    setBusy(true);
                    void teams.leave(t.id).then((done) => {
                      setBusy(false);
                      if (!done.ok) setFailed(reason(done.reason, s));
                    });
                  }}
                >
                  {s.teams.leave}
                </button>
              </div>
            </div>
          ))
        )}
      </section>

      {/* --- Making one ---------------------------------------------------- */}
      {making ? (
        <form className="chat-teams-new" onSubmit={submit}>
          <label className="label" htmlFor="chat-team-name">
            {s.teams.nameLabel}
          </label>
          <input
            id="chat-team-name"
            className="chat-input"
            type="text"
            value={name}
            maxLength={LIMITS.team.name.max}
            autoComplete="off"
            autoFocus
            // A team name is the members' own and may be in either script,
            // like every other field in this panel that holds something a
            // person wrote.
            dir="auto"
            onChange={(e) => setName(e.target.value)}
          />
          <div className="chat-invite-actions">
            <Button type="submit" variant="solid" disabled={busy}>
              {s.teams.make}
            </Button>
            <Button
              type="button"
              variant="quiet"
              onClick={() => {
                setMaking(false);
                setFailed(null);
              }}
            >
              {s.teams.cancel}
            </Button>
          </div>
        </form>
      ) : (
        /* Shown and refused rather than hidden at the limit, which is the
           message menu's rule for the same situation: a control that vanishes
           teaches nobody why it was there. */
        <Button type="button" variant="quiet" disabled={full} title={full ? s.teams.tooMany : undefined} onClick={() => setMaking(true)}>
          {s.teams.new}
        </Button>
      )}

      {failed && (
        <p className="note" role="status">
          {failed}
        </p>
      )}

      {/* The sentence this whole feature has to say. Last, under everything it
          is about, and never conditional. */}
      <p className="note">{s.teams.privacy}</p>
    </div>
  );
}

/**
 * The Worker's reason, in this reader's language.
 *
 * A closed set, matched by the exact words the routes answer with. Anything
 * unrecognised — a Worker a deploy ahead of this bundle — falls back to the
 * general sentence, which is what a tab that does not understand an answer
 * should say rather than showing the raw string.
 */
function reason(raw: string | undefined, s: ReturnType<typeof useStrings>['chat']): string {
  switch (raw) {
    case 'too many teams':
      return s.teams.tooMany;
    case 'team is full':
      return s.teams.teamFull;
    case 'already a member':
      return s.teams.alreadyIn;
    case 'already asked':
      return s.teams.alreadyAsked;
    case 'not a lead':
      return s.teams.notLead;
    case 'bad name':
      return s.teams.badName;
    default:
      return s.teams.failed;
  }
}

export default Teams;
